import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import { formatISODate } from '@client/helpers/date-utils';
import { Async } from '@client/new/helpers/types';
import { BusinessModel } from '@playbook-technologies/common-enums';

export type useSubscriptionPayoutsProps = {
  date: Date;
  creatorId: number;
};

export type EarningsBreakdown = {
  date: string;
  businessModel: BusinessModel;
  limitedMonthlyRevenue?: number;
  monthlyRevenue: number;
  annualRevenue: number;
  limitedAnnualRevenue?: number;
  futureAnnualRevenue?: number;
  futureRefundsAndDisputesRevenue?: number;
  totalFutureRevenue?: number;
  quarterlyRevenue: number;
  engagementRevenue: number;
  refundsAndDisputesRevenue: number;
  subsidy?: number;
  totalSubscriptionRevenue: number;
  payoutFee: number;
  totalPayout: number;
  paidAt?: string;
  hasPrograms: boolean;
  bundles?: BundleEarningsBreakdown[];
};

export type BundleEarningsBreakdown = {
  id: number;
  title: string;
  monthlyRevenue: number;
  annualRevenue: number;
  quarterlyRevenue: number;
  engagementRevenue: number;
  refundsAndDisputesRevenue: number;
  futureAnnualRevenue?: number;
  futureRefundsAndDisputesRevenue?: number;
  totalFutureRevenue?: number;
  totalSubscriptionRevenue: number;
};

const fetchPayoutData = async (
  creatorId: number,
  date: Date,
): Promise<EarningsBreakdown> => {
  const url = new URL(getCreatorApiUrl(`/payout/${creatorId}/dashboard`));

  const response = await axios.get<EarningsBreakdown>(url.toString(), {
    params: {
      date: formatISODate(date),
    },
    headers: {
      Authorization: auth.getToken(),
    },
  });

  return response.data;
};

export const useSubscriptionPayouts = ({
  date,
  creatorId,
}: useSubscriptionPayoutsProps): Async<EarningsBreakdown> => {
  const { isFetched, data, isError } = useQuery(
    ['payout-dashboard', creatorId, date],
    () => fetchPayoutData(creatorId, date),
  );

  if (isError) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (isFetched && data) {
    return {
      type: 'fetched',
      value: data,
    };
  }

  return {
    type: 'fetching',
  };
};
